import { MailCheck, Shield } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { brand } from "@/config/brand";
import { resendOtp, verifyEmailOtp } from "@/features/auth/api";
import { useEmailVerificationStore } from "@/features/auth/store/email-verification-store";
import { ApiError } from "@/lib/api";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * Email-verification step. Reached after a login attempt on an unverified
 * account, which sets `pendingEmail` in the email-verification store.
 * Verifying the OTP logs the user in (the API stores the returned session), so
 * on success we go straight to the dashboard, no separate sign-in.
 *
 * This intentionally uses plain `useState` rather than Formik/`FormField`: the
 * single control is shadcn's `InputOTP` primitive (which `FormField`'s text
 * abstraction doesn't fit) and the only client-side rule is "6 digits" — the
 * real validation is the server checking the code.
 */
export function OtpVerificationForm() {
  const navigate = useNavigate();
  const pendingEmail = useEmailVerificationStore((s) => s.pendingEmail);
  const setPendingEmail = useEmailVerificationStore((s) => s.setPendingEmail);

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // Synchronous re-entrancy guard: `onComplete` and an Enter keypress can both
  // fire in the same tick, before the `submitting` state update flushes.
  const submittingRef = useRef(false);

  // Tick the resend cooldown down to zero; cleaned up on unmount / re-run.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = useCallback(
    async (value: string) => {
      if (value.length !== OTP_LENGTH || submittingRef.current) return;
      submittingRef.current = true;
      setSubmitting(true);
      try {
        await verifyEmailOtp(pendingEmail, value);
        // Clear the pending email so the (PII) address doesn't linger in the
        // store for the rest of the session.
        setPendingEmail("");
        toast.success("Email verified", { description: `Welcome to ${brand.name}.` });
        navigate("/dashboard", { replace: true });
      } catch (err) {
        const message =
          err instanceof ApiError && err.status === 400
            ? "That code is invalid or has expired."
            : "Couldn't verify your code. Please try again.";
        toast.error(message);
        setCode("");
        setSubmitting(false);
        submittingRef.current = false;
      }
    },
    [navigate, pendingEmail, setPendingEmail],
  );

  async function handleResend() {
    if (cooldown > 0) return;
    setCooldown(RESEND_COOLDOWN_SECONDS);
    try {
      await resendOtp(pendingEmail);
      toast.success("Code sent", { description: `We emailed a new code to ${pendingEmail}.` });
    } catch {
      toast.error("Couldn't resend the code. Please try again in a moment.");
      setCooldown(0);
    }
  }

  // No email to verify (e.g. a direct hit or a refresh cleared the store) —
  // send the user back to sign in rather than showing a dead form.
  if (!pendingEmail) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Card className="gap-0 p-6 sm:p-10">
      <div className="mb-8 flex flex-col items-center text-center">
        <div className="bg-primary/10 text-primary mb-4 flex size-12 items-center justify-center rounded-full">
          <MailCheck className="size-6" />
        </div>
        <h1 className="font-heading text-foreground mb-1 text-2xl font-bold">Verify your email</h1>
        <p className="text-muted-foreground text-sm">
          Enter the {OTP_LENGTH}-digit code we sent to{" "}
          <span className="text-foreground font-medium">{pendingEmail}</span>.
        </p>
      </div>

      <form
        className="grid gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          void handleVerify(code);
        }}
      >
        <div className="flex justify-center">
          <InputOTP
            maxLength={OTP_LENGTH}
            value={code}
            onChange={setCode}
            onComplete={(value) => void handleVerify(value)}
            disabled={submitting}
            autoFocus
            containerClassName="gap-2"
          >
            <InputOTPGroup className="gap-2">
              {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key -- fixed-length slot list
                <InputOTPSlot key={i} index={i} className="size-12 rounded-md border text-lg" />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>

        <Button
          type="submit"
          disabled={submitting || code.length !== OTP_LENGTH}
          className="h-11"
        >
          {submitting ? "Verifying..." : "Verify Email"}
        </Button>
      </form>

      <div className="text-muted-foreground mt-6 text-center text-sm">
        Didn&apos;t get a code?{" "}
        <button
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0}
          className="text-primary hover:text-primary/80 font-medium transition-colors disabled:opacity-60"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>

      <div className="text-muted-foreground/60 mt-8 flex items-center justify-center gap-2">
        <Shield className="size-4" />
        <span className="text-xs">Secure Verification</span>
      </div>
    </Card>
  );
}
